/**
 * HTML Sanitization Utility
 * Safely sanitizes HTML content to prevent XSS attacks
 */

import DOMPurify from 'dompurify';

// Configure DOMPurify options
const DEFAULT_CONFIG: DOMPurify.Config = {
  // Allow these tags
  ALLOWED_TAGS: [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr',
    'strong', 'b', 'em', 'i', 'u', 'del', 's', 'mark',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code', 'kbd',
    'a', 'img',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'div', 'span',
    'sup', 'sub',
    'details', 'summary',
    'dl', 'dt', 'dd',
  ],
  
  // Allow these attributes
  ALLOWED_ATTR: [
    'href', 'target', 'rel',
    'src', 'alt', 'title', 'width', 'height',
    'class', 'id',
    'colspan', 'rowspan',
    'start', 'type',
    'data-*', // Allow data attributes
  ],
  
  // Allow these URI schemes
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|xxx):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  
  // Additional security settings
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  
  // Keep data attributes
  ADD_ATTR: ['target'],
  
  // Ensure links open in new tab with security
  ADD_TAGS: [],
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param dirty - The potentially unsafe HTML string
 * @param config - Optional custom DOMPurify configuration
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(
  dirty: string,
  config?: Partial<DOMPurify.Config>
): string {
  // Merge custom config with defaults
  const finalConfig = config ? { ...DEFAULT_CONFIG, ...config } : DEFAULT_CONFIG;
  
  // Sanitize the HTML
  const clean = DOMPurify.sanitize(dirty, finalConfig);
  
  return clean;
}

/**
 * Sanitizes HTML for markdown content
 * Allows additional markdown-specific elements
 */
export function sanitizeMarkdownHtml(dirty: string): string {
  const markdownConfig: DOMPurify.Config = {
    ...DEFAULT_CONFIG,
    ALLOWED_TAGS: [
      ...(DEFAULT_CONFIG.ALLOWED_TAGS || []),
      'figure', 'figcaption', // For images with captions
      'abbr', 'acronym', // For abbreviations
      'cite', 'q', // For citations and quotes
      'time', // For dates
      'var', 'samp', // For code samples
    ],
    ALLOWED_ATTR: [
      ...(DEFAULT_CONFIG.ALLOWED_ATTR || []),
      'datetime', // For time elements
      'cite', // For blockquote citations
      'lang', // For language specification
    ],
  };
  
  return sanitizeHtml(dirty, markdownConfig);
}

/**
 * Sanitizes HTML for rich text editor content
 * Allows styling attributes for editors like TipTap
 */
export function sanitizeEditorHtml(dirty: string): string {
  const editorConfig: DOMPurify.Config = {
    ...DEFAULT_CONFIG,
    ALLOWED_ATTR: [
      ...(DEFAULT_CONFIG.ALLOWED_ATTR || []),
      'style', // Allow inline styles for editor
      'contenteditable', // For editor functionality
      'spellcheck',
    ],
    // Allow style attribute but sanitize its content
    ALLOW_DATA_ATTR: true,
    KEEP_CONTENT: true,
  };
  
  // Additional processing for style attributes
  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName === 'style') {
      // Only allow safe CSS properties
      const allowedStyles = [
        'color', 'background-color',
        'font-size', 'font-weight', 'font-style', 'font-family',
        'text-align', 'text-decoration', 'text-transform',
        'line-height', 'letter-spacing',
        'margin', 'padding',
        'border', 'border-radius',
        'width', 'height', 'max-width', 'max-height',
        'display', 'float', 'clear',
      ];
      
      const styleValue = data.attrValue || '';
      const styles = styleValue.split(';').filter(style => {
        const property = style.split(':')[0]?.trim().toLowerCase();
        return allowedStyles.some(allowed => property?.startsWith(allowed));
      });
      
      data.attrValue = styles.join(';');
    }
  });
  
  const result = sanitizeHtml(dirty, editorConfig);
  
  // Remove the hook after use
  DOMPurify.removeHook('uponSanitizeAttribute');
  
  return result;
}

/**
 * Sanitizes and processes external links
 * Ensures external links open in new tab with proper security
 */
export function sanitizeAndProcessLinks(dirty: string): string {
  // First sanitize the HTML
  let clean = sanitizeHtml(dirty);
  
  // Process links to add security attributes
  if (typeof document !== 'undefined') {
    const temp = document.createElement('div');
    temp.innerHTML = clean;
    
    const links = temp.querySelectorAll('a[href]');
    links.forEach(link => {
      const href = link.getAttribute('href') || '';
      
      // Check if external link
      if (href.startsWith('http') && !href.includes(window.location.hostname)) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
    
    clean = temp.innerHTML;
  }
  
  return clean;
}

/**
 * Strips all HTML tags and returns plain text
 * Useful for previews or text-only contexts
 */
export function stripHtml(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
}

/**
 * Checks if a string contains potentially dangerous HTML
 * Returns true if the content would be modified by sanitization
 */
export function isDangerousHtml(html: string): boolean {
  const sanitized = sanitizeHtml(html);
  return sanitized !== html;
}

// Export configured DOMPurify instance for advanced usage
export { DOMPurify };

// Default export
export default sanitizeHtml;